const PARIS_TIME_ZONE = "Europe/Paris";

type DateInput = string | Date;

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

function getPartsInTimeZone(date: Date, timeZone: string) {
  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const map = Object.fromEntries(parts.map((part) => [part.type, part.value]));

  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second),
  };
}

function getParisOffsetMinutes(date: Date): number {
  const parts = getPartsInTimeZone(date, PARIS_TIME_ZONE);
  const parisAsUtcMs = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  return Math.round((parisAsUtcMs - date.getTime()) / 60000);
}

function toDate(value: DateInput): Date {
  return value instanceof Date ? value : new Date(value);
}

export function getParisDateKey(value: DateInput): string {
  const parts = getPartsInTimeZone(toDate(value), PARIS_TIME_ZONE);
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
}

export function formatParisDate(
  value: DateInput,
  options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }
): string {
  return new Intl.DateTimeFormat("fr-FR", {
    timeZone: PARIS_TIME_ZONE,
    ...options,
  }).format(toDate(value));
}

export function formatParisTime(
  value: DateInput,
  options: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
  }
): string {
  return new Intl.DateTimeFormat("fr-FR", {
    timeZone: PARIS_TIME_ZONE,
    ...options,
  }).format(toDate(value));
}

export function toParisDateInputValue(isoString: string | null): string {
  if (!isoString) return "";
  return getParisDateKey(isoString);
}

export function toParisTimeInputValue(isoString: string | null): string {
  if (!isoString) return "";
  const parts = getPartsInTimeZone(new Date(isoString), PARIS_TIME_ZONE);
  return `${pad2(parts.hour)}:${pad2(parts.minute)}`;
}

/**
 * Convertit une date/heure de Paris (inputs YYYY-MM-DD + HH:mm)
 * vers un timestamp UTC ISO stockable en base.
 */
export function parisDateTimeToUtcIso(dateStr: string, timeStr: string): string {
  if (!dateStr || !timeStr) return "";

  const [year, month, day] = dateStr.split("-").map(Number);
  const [hour, minute] = timeStr.split(":").map(Number);

  const localAsUtcMs = Date.UTC(year, month - 1, day, hour, minute, 0);

  let offsetMinutes = getParisOffsetMinutes(new Date(localAsUtcMs));
  let utcMs = localAsUtcMs - offsetMinutes * 60_000;

  const adjustedOffsetMinutes = getParisOffsetMinutes(new Date(utcMs));
  if (adjustedOffsetMinutes !== offsetMinutes) {
    utcMs = localAsUtcMs - adjustedOffsetMinutes * 60_000;
  }

  return new Date(utcMs).toISOString();
}

export function toDateKeyFromLocalDate(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function dateKeyToUtcMidnightMs(dateKey: string): number | null {
  const match = dateKey.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return Date.UTC(year, month - 1, day);
}

/**
 * Différence en jours calendaires (référentiel Europe/Paris).
 * Exemple: 0 = même jour, 1 = demain, -1 = hier.
 */
export function diffParisCalendarDays(from: DateInput, to: DateInput): number {
  const fromMs = dateKeyToUtcMidnightMs(getParisDateKey(from));
  const toMs = dateKeyToUtcMidnightMs(getParisDateKey(to));

  if (fromMs === null || toMs === null) return 0;
  return Math.round((toMs - fromMs) / 86_400_000);
}
